/** Provides drag-to-resize state for a fixed right-side panel. */
import React, { createContext, useCallback, useContext, useState } from "react";

import { clamp } from "lodash";
import { useEventListener, useLocalStorage } from "usehooks-ts";

const MIN_WIDTH = 420;
const MAX_WIDTH = 800;

type ResizableSidepaneContextType = {
  width: number;
  isResizing: boolean;
  handleResizeStart: () => void;
};

const ResizableSidepaneContext = createContext<
  ResizableSidepaneContextType | undefined
>(undefined);

export const useResizableSidepane = (): ResizableSidepaneContextType => {
  const context = useContext(ResizableSidepaneContext);
  if (!context) {
    throw new Error(
      "useResizableSidepane must be used within a ResizableSidepaneProvider",
    );
  }
  return context;
};

/**
 * Drag handle rendered on the left edge of the sidepane.
 * Must be used inside a ResizableSidepaneProvider.
 */
export const ResizeHandle: React.FC = () => {
  const { handleResizeStart } = useResizableSidepane();

  return (
    <button
      className="absolute bottom-0 left-0 h-full w-1 cursor-col-resize"
      onMouseDown={handleResizeStart}
    />
  );
};

type ResizableSidepaneProviderProps = {
  children: React.ReactNode;
  defaultWidth?: number;
  maxWidth?: number;
  storageKey?: string;
};

export const ResizableSidepaneProvider: React.FC<
  ResizableSidepaneProviderProps
> = ({ children, defaultWidth, maxWidth, storageKey }) => {
  const effectiveMaxWidth = maxWidth ?? MAX_WIDTH;
  const effectiveDefaultWidth =
    defaultWidth ?? (MIN_WIDTH + effectiveMaxWidth) / 2;
  const [width, setWidth] = useLocalStorage<number>(
    storageKey ?? "resizable-sidepane-width",
    effectiveDefaultWidth,
  );
  const clampedWidth = clamp(width, MIN_WIDTH, effectiveMaxWidth);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  useEventListener("mousemove", (event: MouseEvent) => {
    if (!isResizing) return;
    setWidth(
      clamp(window.innerWidth - event.clientX, MIN_WIDTH, effectiveMaxWidth),
    );
  });

  useEventListener("mouseup", () => {
    if (!isResizing) return;
    setIsResizing(false);
  });

  return (
    <ResizableSidepaneContext.Provider
      value={{ width: clampedWidth, isResizing, handleResizeStart }}
    >
      {children}
    </ResizableSidepaneContext.Provider>
  );
};
